import { apiClient, handleApiRequest } from './client'

// ==================== User Management ====================

export interface UserRoleInfo {
  id: string
  email: string
  first_name: string
  last_name: string
  role: string
  phone: string | null
  teacher_code: string | null
  is_active: boolean
  created_at: string
}

export interface UserListResponse {
  users: UserRoleInfo[]
  total: number
  has_more: boolean
}

export interface UserCreateRequest {
  email: string
  first_name?: string
  last_name?: string
  role: string
  phone?: string
  teacher_code?: string
  notes?: string
}

export interface RoleActionResponse {
  message: string
  user: UserRoleInfo
}

export interface SuperadminHolderInfo {
  id: string
  email: string
  first_name: string
  last_name: string
  role: string
  roles: string[]
  is_active: boolean
  created_at: string
}

export const superadminApi = {
  // Role management
  assignRole: (data: UserCreateRequest) =>
    handleApiRequest<RoleActionResponse>(apiClient.post('/api/v1/superadmin/users/role', data)),

  promoteToSuperadmin: (data: { email: string; first_name?: string; last_name?: string; notes?: string }) =>
    handleApiRequest<RoleActionResponse>(apiClient.post('/api/v1/superadmin/users/promote-superadmin', data)),

  listUsers: (params?: { role?: string; skip?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.role) qs.set('role', params.role)
    if (params?.skip !== undefined) qs.set('skip', String(params.skip))
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<UserListResponse>(apiClient.get(`/api/v1/superadmin/users${query}`))
  },

  removeRole: (userId: string, reason?: string) => {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : ''
    return handleApiRequest(apiClient.delete(`/api/v1/superadmin/users/${userId}/role${query}`))
  },

  removeSpecificRole: (userId: string, role: string, reason?: string) => {
    const params = new URLSearchParams({ role })
    if (reason) params.set('reason', reason)
    return handleApiRequest(
      apiClient.delete(`/api/v1/superadmin/users/${userId}/role?${params.toString()}`),
    )
  },

  // Direktor-Perspektive: Superadmin- und Direktor-Inhaber der eigenen Schule
  listSuperadminHolders: () =>
    handleApiRequest<SuperadminHolderInfo[]>(apiClient.get('/api/v1/superadmin/users/superadmins')),

  demoteUser: (userId: string, targetRole: 'admin' | 'teacher', reason?: string) =>
    handleApiRequest<UserRoleInfo>(
      apiClient.patch(`/api/v1/superadmin/users/${userId}/demote`, {
        target_role: targetRole,
        reason: reason || undefined,
      }),
    ),

  // Teacher parameters (routed to /teachers endpoints — superadmin does not host these)
  createBackground: (data: { name: string; code?: string; description?: string }) =>
    handleApiRequest(apiClient.post('/api/v1/teachers/backgrounds', data)),

  listBackgrounds: () =>
    handleApiRequest<{ id: string; name: string; code: string | null; description: string | null; is_active: boolean }[]>(
      apiClient.get('/api/v1/teachers/backgrounds'),
    ),

  createSubject: (data: { name: string; code?: string; description?: string }) =>
    handleApiRequest(apiClient.post('/api/v1/teachers/subjects', data)),

  listSubjects: () =>
    handleApiRequest<{ id: string; name: string; code: string | null; description: string | null; is_active: boolean }[]>(
      apiClient.get('/api/v1/teachers/subjects'),
    ),

  createFaculty: (data: { name: string; code?: string; description?: string; color?: string }) =>
    handleApiRequest(apiClient.post('/api/v1/teachers/faculties', data)),

  listFaculties: () =>
    handleApiRequest<{ id: string; name: string; code: string | null; description: string | null; color: string | null; is_active: boolean }[]>(
      apiClient.get('/api/v1/teachers/faculties'),
    ),

  // Import conflict preview
  previewImportConflicts: (entityType: 'teacher' | 'holiday', records: Record<string, unknown>[]) =>
    handleApiRequest<{ total_records: number; conflict_count: number; conflicts: Array<{
      record_id: string; record_key: string; field: string; label: string; existing: string | null; incoming: string | null;
    }> }>(
      apiClient.post('/api/v1/superadmin/import/preview-conflicts', { entity_type: entityType, records }),
    ),

  // SCHOOL DESIGN LOGO (Stamm; persistiert sofort)
  designLogo: {
    upload: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return handleApiRequest<{ logo_data: string; file_size: number; content_type: string }>(
        apiClient.post('/api/v1/superadmin/design/logo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
      )
    },
    remove: () =>
      handleApiRequest<{ message: string }>(apiClient.delete('/api/v1/superadmin/design/logo')),
  },

  getTeacherSettingsPreview: () =>
    handleApiRequest(apiClient.get('/api/v1/superadmin/teacher-settings-preview')),

  getTeacherSettingAuditLog: (params?: { limit?: number; offset?: number; action?: string; setting_key?: string }) => {
    const qs = new URLSearchParams()
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    if (params?.offset !== undefined) qs.set('offset', String(params.offset))
    if (params?.action) qs.set('action', params.action)
    if (params?.setting_key) qs.set('setting_key', params.setting_key)
    return handleApiRequest(apiClient.get(`/api/v1/superadmin/teacher-setting-audit-log?${qs}`))
  },
}

export const designLogoApi = superadminApi.designLogo

// ==================== Engineer Management ====================

export interface EngineerScopeInfo {
  id: string
  user_id: string
  email: string
  first_name: string
  last_name: string
  scopes: string[]
  expires_at: string | null
  is_active: boolean
  token_used: boolean
  is_expired: boolean
  kind: 'intern' | 'extern' | 'partner'
  stammrolle: string | null
  created_at: string
  invited_by_name: string | null
}

export interface EngineerDetailInfo extends EngineerScopeInfo {
  permissions: string[]
  scopes_version: number
}

export interface EngineerListResponse {
  engineers: EngineerScopeInfo[]
  total: number
}

export interface EngineerCreateRequest {
  email: string
  first_name?: string
  last_name?: string
  scopes: string[]
  token_validity_hours: number
}

export interface EngineerUpdateRequest {
  first_name?: string
  last_name?: string
  scopes?: string[]
  is_active?: boolean
}

export interface ScopesConfigResponse {
  accordions: Array<{
    role: string
    exclusive: Array<{ key: string; label: string }>
    full_role: Array<{ key: string; label: string }>
  }>
  valid_scopes: string[]
  token_validity_options: Record<string, number>
  max_validity_hours: number
  min_validity_hours: number
}

export const engineerApi = {
  getScopesConfig: () =>
    handleApiRequest<ScopesConfigResponse>(apiClient.get('/api/v1/superadmin/engineers/scopes-config')),

  list: (params?: { status_filter?: string; search?: string; skip?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.status_filter) qs.set('status_filter', params.status_filter)
    if (params?.search) qs.set('search', params.search)
    if (params?.skip !== undefined) qs.set('skip', String(params.skip))
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<EngineerListResponse>(apiClient.get(`/api/v1/superadmin/engineers/${query}`))
  },

  create: (data: EngineerCreateRequest) =>
    handleApiRequest<EngineerDetailInfo>(apiClient.post('/api/v1/superadmin/engineers/', data)),

  get: (id: string) =>
    handleApiRequest<EngineerDetailInfo>(apiClient.get(`/api/v1/superadmin/engineers/${id}`)),

  update: (id: string, data: EngineerUpdateRequest) =>
    handleApiRequest<EngineerDetailInfo>(apiClient.put(`/api/v1/superadmin/engineers/${id}`, data)),

  delete: (id: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/superadmin/engineers/${id}`)),

  regenerateToken: (id: string) =>
    handleApiRequest<EngineerDetailInfo>(apiClient.post(`/api/v1/superadmin/engineers/${id}/regenerate-token`)),

  activate: (id: string) =>
    handleApiRequest<EngineerDetailInfo>(apiClient.post(`/api/v1/superadmin/engineers/${id}/activate`)),

  deactivate: (id: string) =>
    handleApiRequest<EngineerDetailInfo>(apiClient.post(`/api/v1/superadmin/engineers/${id}/deactivate`)),

  extend: (id: string, token_validity_hours: number) =>
    handleApiRequest<EngineerDetailInfo>(apiClient.post(`/api/v1/superadmin/engineers/${id}/extend`, { token_validity_hours })),
}

// ==================== Email Templates ====================

export interface EmailTemplateListItem {
  id: string
  name: string
  template_key: string
  category: string
  description: string | null
  version: number
  is_active: boolean
  is_system: boolean
  has_override: boolean
  language: string
  required_variables: string[] | null
  updated_at: string
  system_template_id: string
}

export interface EmailTemplateDetail {
  id: string
  school_id: string | null
  name: string
  template_key: string
  category: string
  description: string | null
  subject_template: string
  body_html_template: string
  body_text_template: string | null
  action_buttons: Record<string, unknown>[] | null
  required_variables: string[] | null
  optional_variables: string[] | null
  version: number
  is_active: boolean
  is_system: boolean
  language: string
  css_styles: string | null
  branding_config: Record<string, unknown> | null
  branding_source: 'school_design' | 'custom'
  created_by_id: string | null
  created_at: string
  updated_at: string
}

export interface EmailTemplateRenderPreview {
  subject: string
  body_html: string
  body_text: string | null
  variables_used: string[]
  variables_missing: string[]
  parse_errors: string[]
}

export const emailTemplateApi = {
  listEmailTemplates: () =>
    handleApiRequest<EmailTemplateListItem[]>(
      apiClient.get('/api/v1/superadmin/email-templates')
    ),

  getEmailTemplate: (id: string) =>
    handleApiRequest<EmailTemplateDetail>(
      apiClient.get(`/api/v1/superadmin/email-templates/${id}`)
    ),

  forkEmailTemplate: (id: string) =>
    handleApiRequest<EmailTemplateDetail>(
      apiClient.post(`/api/v1/superadmin/email-templates/${id}/fork`)
    ),

  updateEmailTemplate: (id: string, data: Partial<{
    name: string; description: string; subject_template: string;
    body_html_template: string; body_text_template: string;
    action_buttons: Record<string, unknown>[]; is_active: boolean;
    css_styles: string; branding_config: Record<string, unknown>;
    branding_source: 'school_design' | 'custom';
  }>) =>
    handleApiRequest<EmailTemplateDetail>(
      apiClient.patch(`/api/v1/superadmin/email-templates/${id}`, data)
    ),

  renderEmailPreview: (
    id: string,
    variables: Record<string, unknown>,
    draft?: Partial<{
      subject_template: string
      body_html_template: string
      body_text_template: string | null
      css_styles: string | null
      branding_config: Record<string, unknown> | null
      branding_source: 'school_design' | 'custom'
    }>,
  ) =>
    handleApiRequest<EmailTemplateRenderPreview>(
      apiClient.post(`/api/v1/superadmin/email-templates/${id}/render`, { variables, ...draft })
    ),

  resetEmailTemplate: (id: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post(`/api/v1/superadmin/email-templates/${id}/reset`)
    ),

  resetAllEmailTemplates: () =>
    handleApiRequest<{ message: string; reset_count: number }>(
      apiClient.post('/api/v1/superadmin/email-templates/reset-all', { confirm: true })
    ),
}
